// services/admin/adminApi.ts
// Instância axios própria do Admin — token/cookies separados da sessão do cliente
// para não colidir com services/api.ts (usado pelo app do cliente).
import axios, { AxiosError, AxiosRequestConfig } from "axios";

export const ADMIN_ACCESS_TOKEN_KEY = "INTREX_ADMIN_ACCESS_TOKEN";

const API_BASE = process.env?.REACT_APP_API_URL || undefined;

export const ADMIN_API = axios.create({
  baseURL: API_BASE ? API_BASE.replace(/\/$/, "") : undefined,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

ADMIN_API.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  const token = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let waiters: Array<(ok: boolean) => void> = [];

async function refreshAdminAccess(): Promise<boolean> {
  try {
    await ADMIN_API.post("/api/admin/auth/refresh");
    return true;
  } catch {
    return false;
  }
}

const NO_RETRY_URLS = ["/api/admin/login", "/api/admin/auth/refresh"];

ADMIN_API.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = (error.config ?? {}) as AxiosRequestConfig & { _retry?: boolean };
    const status = error?.response?.status ?? 0;
    const url = original.url || "";

    if (status !== 401) return Promise.reject(error);
    if (NO_RETRY_URLS.some((p) => url.includes(p))) return Promise.reject(error);
    if (original._retry) return Promise.reject(error);
    original._retry = true;

    if (isRefreshing) {
      const ok = await new Promise<boolean>((resolve) => waiters.push(resolve));
      if (!ok) return Promise.reject(error);
      return ADMIN_API(original);
    }

    isRefreshing = true;
    const ok = await refreshAdminAccess();
    isRefreshing = false;
    waiters.forEach((fn) => fn(ok));
    waiters = [];

    // Não force nenhuma navegação aqui: essa checagem roda em qualquer página
    // do site (AdminAuthProvider consulta /api/admin/me no mount), então um 401
    // aqui é o estado normal para quem não é admin. Quem decide redirecionar
    // é o AdminProtectedRoute (via React Router), nunca window.location direto.
    if (!ok) {
      return Promise.reject(error);
    }

    return ADMIN_API(original);
  }
);
